import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { investmentSheetCraService } from '../../services/investmentSheetService';
import { investorService } from '../../services/userService';
import { craProductService } from '../../services/productService';
import { craPhaseService } from '../../services/phaseService';
import { InvestmentSheetCra } from '../../types/InvestmentSheet';
import { Investor } from '../../types/User';
import { CraProduct } from '../../types/InvestmentProduct';
import { CraPhase } from '../../types/Phase';
import InvestorAutocomplete from '../common/InvestorAutocomplete';
import PdfUpload from '../common/PdfUpload';
import '../users/UserForm.css';

interface InvestmentCraFormProps {
  sheet: InvestmentSheetCra | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const InvestmentCraForm: React.FC<InvestmentCraFormProps> = ({ sheet, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<Partial<InvestmentSheetCra>>({
    investorId: '',
    productId: '',
    phaseId: '',
    importe: 0,
    origenFondos: '',
    titularCuentaDevolucion: '',
    ibanDevolucion: '',
    estado: '',
    estadoKyc: '',
  });

  const [investors, setInvestors] = useState<Investor[]>([]);
  const [products, setProducts] = useState<CraProduct[]>([]);
  const [phases, setPhases] = useState<CraPhase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvestors();
    loadProducts();
    if (sheet) {
      setFormData(sheet);
      if (sheet.productId) {
        loadPhases(sheet.productId);
      }
    }
  }, [sheet]);

  const loadInvestors = async () => {
    try {
      const response = await investorService.getAll();
      setInvestors(response.data);
    } catch (err) {
      console.error('Error loading investors:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await craProductService.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadPhases = async (productId: string) => {
    try {
      const response = await craPhaseService.getAll(productId);
      setPhases(response.data);
    } catch (err) {
      console.error('Error loading phases:', err);
    }
  };

  const handleInvestorChange = (investorId: string) => {
    setFormData((prev) => ({
      ...prev,
      investorId: investorId,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'productId') {
      setFormData((prev) => ({
        ...prev,
        productId: value,
        phaseId: '',
      }));
      if (value) {
        loadPhases(value);
      } else {
        setPhases([]);
      }
    } else if (name === 'importe') {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? parseFloat(value) : undefined,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (sheet?.id) {
        await investmentSheetCraService.update(sheet.id, formData);
        showToast('Inversión CRA actualizada correctamente');
      } else {
        await investmentSheetCraService.create(formData);
        showToast('Inversión CRA creada correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la ficha de inversión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form">
      <h3>{sheet ? 'Editar Inversión CRA' : 'Crear Inversión CRA'}</h3>
      <p className="form-subtitle">Participativo - Inversión en participaciones con revalorización</p>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="investorId">Inversor *</label>
            <InvestorAutocomplete
              investors={investors}
              value={formData.investorId || ''}
              onChange={handleInvestorChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="productId">Producto *</label>
            <select
              id="productId"
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar...</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.nombreProducto}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="phaseId">Fase *</label>
            <select
              id="phaseId"
              name="phaseId"
              value={formData.phaseId}
              onChange={handleChange}
              required
              disabled={!formData.productId}
            >
              <option value="">Seleccionar...</option>
              {phases.map((phase) => (
                <option key={phase.id} value={phase.id}>
                  {phase.nombreFase}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h4>Económico</h4>
        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="importe">Importe *</label>
            <input
              type="number"
              id="importe"
              name="importe"
              value={formData.importe || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              required
            />
          </div>
          {sheet && (
            <div className="form-group">
              <label>Número de Tickets</label>
              <input
                type="number"
                value={formData.numTickets || 0}
                disabled
              />
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="origenFondos">Origen de Fondos</label>
            <input
              type="text"
              id="origenFondos"
              name="origenFondos"
              value={formData.origenFondos || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="titularCuentaDevolucion">Titular Cuenta Devolución</label>
            <input
              type="text"
              id="titularCuentaDevolucion"
              name="titularCuentaDevolucion"
              value={formData.titularCuentaDevolucion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="ibanDevolucion">IBAN Devolución</label>
            <input
              type="text"
              id="ibanDevolucion"
              name="ibanDevolucion"
              value={formData.ibanDevolucion || ''}
              onChange={handleChange}
              maxLength={34}
            />
          </div>
        </div>

        <h4>Estado y Cumplimiento</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="estado">Estado</label>
            <input
              id="estado"
              name="estado"
              value={formData.estado || 'Prospecto'}
              disabled
            />
          </div>
          <div className="form-group">
            <label htmlFor="estadoKyc">Estado KYC</label>
            <select
              id="estadoKyc"
              name="estadoKyc"
              value={formData.estadoKyc || ''}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Pendiente">Pendiente</option>
              <option value="En revisión">En revisión</option>
              <option value="Aprobado">Aprobado</option>
              <option value="Rechazado">Rechazado</option>
            </select>
          </div>
        </div>

        <h4>Flujo de Gestión</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="solicitaContrato">Solicita Contrato</label>
            <input
              type="date"
              id="solicitaContrato"
              name="solicitaContrato"
              value={formData.solicitaContrato || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaContrato">Fecha Contrato</label>
            <input
              type="date"
              id="fechaContrato"
              name="fechaContrato"
              value={formData.fechaContrato || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaFirmaCliente">Firma Cliente</label>
            <input
              type="date"
              id="fechaFirmaCliente"
              name="fechaFirmaCliente"
              value={formData.fechaFirmaCliente || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaFirmaClub">Firma Club</label>
            <input
              type="date"
              id="fechaFirmaClub"
              name="fechaFirmaClub"
              value={formData.fechaFirmaClub || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaTransRecibida">Transferencia Recibida</label>
            <input
              type="date"
              id="fechaTransRecibida"
              name="fechaTransRecibida"
              value={formData.fechaTransRecibida || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaDevCapital">Devolución Capital</label>
            <input
              type="date"
              id="fechaDevCapital"
              name="fechaDevCapital"
              value={formData.fechaDevCapital || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaLiquidacion">Fecha Liquidación</label>
            <input
              type="date"
              id="fechaLiquidacion"
              name="fechaLiquidacion"
              value={formData.fechaLiquidacion || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaRechazado">Fecha Rechazado</label>
            <input
              type="date"
              id="fechaRechazado"
              name="fechaRechazado"
              value={formData.fechaRechazado || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {sheet?.id && (
          <>
            <h4>Documentos</h4>
            <div className="form-row">
              <div className="form-group">
                <PdfUpload
                  sheetId={sheet.id}
                  docType="contrato"
                  currentFile={formData.contratoPdf}
                  label="Contrato"
                  onUploadSuccess={(key) => setFormData((prev) => ({ ...prev, contratoPdf: key }))}
                  onDeleteSuccess={() => setFormData((prev) => ({ ...prev, contratoPdf: undefined }))}
                />
              </div>
              <div className="form-group">
                <PdfUpload
                  sheetId={sheet.id}
                  docType="transferencia"
                  currentFile={formData.transferenciaPdf}
                  label="Transferencia"
                  onUploadSuccess={(key) => setFormData((prev) => ({ ...prev, transferenciaPdf: key }))}
                  onDeleteSuccess={() => setFormData((prev) => ({ ...prev, transferenciaPdf: undefined }))}
                />
              </div>
            </div>
          </>
        )}

        {sheet && (
          <>
            <h4>Métricas Calculadas (Solo lectura)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Capital Devuelto</label>
                <input
                  type="text"
                  value={formatCurrency(formData.capitalDevuelto)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Valor Nominal Inversión</label>
                <input
                  type="text"
                  value={formatCurrency(formData.valorNominalInversion)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Valor Actual Inversión</label>
                <input
                  type="text"
                  value={formatCurrency(formData.valorActualInversion)}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Plusvalía Latente</label>
                <input
                  type="text"
                  value={formatCurrency(formData.plusvaliaLatente)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>% Reval. Total</label>
                <input
                  type="text"
                  value={formatPercent(formData.porcentajeRevalTotal)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Repartos Cobrados</label>
                <input
                  type="text"
                  value={formatCurrency(formData.repartosCobrados)}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Liq. Final Pendiente</label>
                <input
                  type="text"
                  value={formatCurrency(formData.liqFinalPendiente)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Rto. Anual Actual</label>
                <input
                  type="text"
                  value={formatPercent(formData.rtoAnualActual)}
                  disabled
                />
              </div>
            </div>

          </>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : sheet ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export default InvestmentCraForm;
